import React, { useState } from 'react';
import { useMembership } from '../../context/MembershipContext';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { FeatureGuard, MultiFeatureGuard } from './FeatureGuard';
import MembershipStatus from './MembershipStatus';
import UpgradeModal from './UpgradeModal';
import { FaCrown, FaUser, FaLock, FaCheck, FaStar, FaRocket } from 'react-icons/fa';

const MembershipTestPage = () => {
  const { isPremium, isFree, membershipStatus } = useMembership();
  const { canAccess, getAvailableFeatures } = useFeatureAccess();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Características según la tabla de requerimientos
  const memberFeatures = [
    'profile_creation',
    'virtual_consultation', 
    'salutio_logo_usage',
    'epayco_payment',
    'revive_free_courses',
    'allies_access',
    'profile_visibility',
    'tech_features'
  ];

  const userFeatures = [
    'professional_directory',
    'virtual_consultation',
    'allies_benefits',
    'tech_features'
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            🧪 Página de Prueba - Sistema de Membresía
          </h1>
          <p className="text-lg text-gray-600">
            Esta página demuestra todas las funcionalidades del sistema free-premium
          </p>
        </div>

        {/* Estado de Membresía */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2">
            <MembershipStatus 
              showUpgradeButton={true}
              className="h-full"
            />
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Información del Sistema
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Plan actual:</span>
                <span className={`font-medium px-2 py-1 rounded text-sm ${
                  isPremium ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {isPremium ? 'Premium' : 'Gratuito'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Características disponibles:</span>
                <span className="font-medium">
                  {getAvailableFeatures().length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Última actualización:</span>
                <span className="font-medium text-sm">
                  {membershipStatus?.lastUpdated 
                    ? new Date(membershipStatus.lastUpdated).toLocaleDateString('es-ES')
                    : 'N/A'
                  }
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Pruebas de Características */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Características para Miembros (Doctores) */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <FaUser className="mr-2 text-blue-600" />
              Características para Miembros (Doctores)
            </h2>
            
            <div className="space-y-4">
              {/* Características Free */}
              <div>
                <h3 className="text-lg font-semibold text-green-700 mb-3">✅ Plan Gratuito</h3>
                <ul className="space-y-2">
                  <li className="flex items-center text-sm">
                    <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                    <span>Creación de perfil</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                    <span>Uso de consultorio virtual</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                    <span>Uso epayco</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                    <span>Acceso a cursos gratuitos de Revive</span>
                  </li>
                </ul>
              </div>

              {/* Características Premium */}
              <div>
                <h3 className="text-lg font-semibold text-yellow-700 mb-3 flex items-center">
                  <FaCrown className="mr-2" />
                  Plan Premium
                </h3>
                
                <FeatureGuard feature="salutio_logo_usage">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center">
                      <FaCheck className="text-green-500 mr-2" />
                      <span className="text-green-800 font-medium">Uso de logo de Redplika</span>
                    </div>
                  </div>
                </FeatureGuard>

                <FeatureGuard feature="allies_access">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center">
                      <FaCheck className="text-green-500 mr-2" />
                      <span className="text-green-800 font-medium">Ingreso a los aliados</span>
                    </div>
                  </div>
                </FeatureGuard>

                <FeatureGuard feature="profile_visibility">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center">
                      <FaCheck className="text-green-500 mr-2" />
                      <span className="text-green-800 font-medium">Perfil visible a los usuarios</span>
                    </div>
                  </div>
                </FeatureGuard>

                <FeatureGuard feature="tech_features">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center">
                      <FaCheck className="text-green-500 mr-2" />
                      <span className="text-green-800 font-medium">Usos tecnológicos (chat bot, agenda en celular)</span>
                    </div>
                  </div>
                </FeatureGuard>
              </div>
            </div>
          </div>

          {/* Características para Usuarios (Pacientes) */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <FaUser className="mr-2 text-purple-600" />
              Características para Usuarios (Pacientes)
            </h2>
            
            <div className="space-y-4">
              {/* Características Free */}
              <div>
                <h3 className="text-lg font-semibold text-green-700 mb-3">✅ Plan Gratuito</h3>
                <ul className="space-y-2">
                  <li className="flex items-center text-sm">
                    <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                    <span>Acceso al directorio de profesionales</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                    <span>Uso del consultorio virtual</span>
                  </li>
                </ul>
              </div>

              {/* Características Premium */}
              <div>
                <h3 className="text-lg font-semibold text-yellow-700 mb-3 flex items-center">
                  <FaCrown className="mr-2" />
                  Plan Premium
                </h3>
                
                <FeatureGuard feature="allies_benefits">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center">
                      <FaCheck className="text-green-500 mr-2" />
                      <span className="text-green-800 font-medium">Acceso a aliados para beneficios</span>
                    </div>
                  </div>
                </FeatureGuard>

                <FeatureGuard feature="tech_features">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                    <div className="flex items-center">
                      <FaCheck className="text-green-500 mr-2" />
                      <span className="text-green-800 font-medium">Usos tecnológicos (chat bot, agenda en celular)</span>
                    </div>
                  </div>
                </FeatureGuard>
              </div>
            </div>
          </div>
        </div>

        {/* Pruebas de FeatureGuard */}
        <div className="mt-8 bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <FaRocket className="mr-2 text-blue-600" />
            Pruebas de Protección de Características
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Prueba individual */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Característica Individual</h3>
              <FeatureGuard feature="tech_features">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <FaCheck className="text-green-500 mr-2" />
                    <span className="text-green-800 font-medium">
                      ¡Tienes acceso a características tecnológicas!
                    </span>
                  </div>
                  <p className="text-green-700 mt-2 text-sm">
                    Chat bot y agenda móvil disponibles.
                  </p>
                </div>
              </FeatureGuard>
            </div>

            {/* Prueba múltiple */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Múltiples Características</h3>
              <MultiFeatureGuard 
                features={['allies_access', 'profile_visibility']}
                requireAll={false}
              >
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <FaStar className="text-blue-500 mr-2" />
                    <span className="text-blue-800 font-medium">
                      Acceso a aliados o visibilidad de perfil
                    </span>
                  </div>
                  <p className="text-blue-700 mt-2 text-sm">
                    Tienes acceso a al menos una de estas características premium.
                  </p>
                </div>
              </MultiFeatureGuard>
            </div>
          </div>
        </div>

        {/* Botón de Upgrade */}
        {isFree && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white font-bold rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all transform hover:scale-105 shadow-lg"
            >
              <FaCrown className="mr-3 text-xl" />
              Actualizar a Premium
            </button>
            <p className="text-gray-600 mt-4">
              Desbloquea todas las características premium y maximiza tu experiencia
            </p>
          </div>
        )}

        {/* Modal de Upgrade */}
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          onUpgrade={(plan) => {
            console.log('Plan actualizado:', plan);
            setShowUpgradeModal(false);
            // Aquí se podría recargar la página o actualizar el estado
            window.location.reload();
          }}
        />
      </div>
    </div>
  );
};

export default MembershipTestPage; 